import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function StatusPanel({ statusData, total, statusColorMap, onMetricClick }) {
  const theme = useTheme();

  let angle = 0;
  const gradientParts = statusData.map(({ status, count }) => {
    const color = statusColorMap[status] || theme.palette.primary.main;
    const sweep = (count / total) * 360;
    const start = angle;
    const end = angle + sweep;
    angle = end;
    return `${color} ${start}deg ${end}deg`;
  });
  const gradient = `conic-gradient(${gradientParts.join(", ")})`;

  const styles = {
    panel: {
      bgcolor: "background.cardBackground",
      border: 1,
      borderColor: "background.tableHeader",
      borderRadius: 2,
      p: 2.5,
      minWidth: 0,
      overflow: "hidden",
    },
    title: {
      fontSize: "0.75rem",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      fontWeight: 700,
      color: "background.textMuted",
      mb: 2,
    },
    donutWrap: {
      display: "flex",
      alignItems: "center",
      gap: 2.5,
    },
    donut: {
      position: "relative",
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: gradient,
      flexShrink: 0,
    },
    hole: {
      position: "absolute",
      width: 64,
      height: 64,
      bgcolor: "background.cardBackground",
      borderRadius: "50%",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    },
    legend: {
      display: "flex",
      flexDirection: "column",
      gap: 1.125,
      flex: 1,
      minWidth: 0,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      cursor: "pointer",
      borderRadius: 0.5,
      "&:hover": { bgcolor: "action.hover" },
    },
    swatch: {
      width: 8,
      height: 8,
      borderRadius: 0.25,
      flexShrink: 0,
    },
    rowLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: "0.875rem",
      color: "background.text",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rowCount: {
      fontSize: "0.9375rem",
      fontWeight: 700,
      color: "background.text",
      fontVariantNumeric: "tabular-nums",
      minWidth: 14,
      textAlign: "right",
    },
    rowPct: {
      fontSize: "0.75rem",
      color: "background.textMuted",
      minWidth: 30,
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
  };

  return (
    <Box sx={styles.panel}>
      <Typography sx={styles.title}>Status Distribution</Typography>
      <Box sx={styles.donutWrap}>
        <Box sx={styles.donut}>
          <Box sx={styles.hole} />
        </Box>
        <Box sx={styles.legend}>
          {statusData.map(({ status, count }) => {
            const color = statusColorMap[status] || theme.palette.primary.main;
            const swatchSx = { ...styles.swatch, bgcolor: color };
            const pct = Math.round((count / total) * 100);
            return (
              <Box key={status} sx={styles.row} onClick={() => onMetricClick("status", status)}>
                <Box sx={swatchSx} />
                <Typography sx={styles.rowLabel}>{status}</Typography>
                <Typography sx={styles.rowCount}>{count}</Typography>
                <Typography sx={styles.rowPct}>{pct}%</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
